import { Link } from '@tanstack/react-router'
import { Coins } from 'lucide-react'
import { useAdminBypass } from '~/app/admin-bypass'
import { type CreditCostState, creditCostState } from '~/entities/credits/lib/credit-cost'
import { requestCreditTopUp } from '~/entities/credits/model/credit-top-up'
import { useCreditPack } from '~/entities/credits/model/use-credit-pack'
import { useCreditsBalance } from '~/entities/credits/model/use-credits'
import { formatCredits } from '~/entities/subscription/plans'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'

interface CreditCost {
  state: CreditCostState
  balance: number | undefined
}

/** What a paid action costs this business right now, and whether the balance covers it. */
export function useCreditCost(businessId: string, cost: number): CreditCost {
  const { data } = useCreditsBalance(businessId)
  const { isBypassing } = useAdminBypass()
  return { state: creditCostState(data?.credits, cost, isBypassing), balance: data?.credits }
}

/** The price on a paid button, e.g. "Cover anpassen · 400 Credits"; nothing for free actions. */
export function CreditCostTag({ cost, className }: { cost: number; className?: string }) {
  const { isBypassing } = useAdminBypass()
  if (cost <= 0 || isBypassing) return null
  return (
    <span className={cn('tabular-nums opacity-70', className)}>
      {m.creditCostTag({ cost: formatCredits(cost) })}
    </span>
  )
}

interface CreditCostNoteProps {
  businessId: string
  cost: number
  className?: string
}

/**
 * The cost and the remaining balance next to a paid action, only when the balance does not cover
 * it (or an admin pays nothing): otherwise the price on the button says it all.
 */
export function CreditCostNote({ businessId, cost, className }: CreditCostNoteProps) {
  const { state, balance } = useCreditCost(businessId, cost)
  const pack = useCreditPack()
  if (cost <= 0 || (state !== 'short' && state !== 'free')) return null
  return (
    <div className={cn('text-muted-foreground text-sm', className)}>
      <p>
        {state === 'free'
          ? m.creditCostAdminFree()
          : balance === undefined
            ? m.creditCostOnly({ cost: formatCredits(cost) })
            : m.creditCostWithBalance({
                cost: formatCredits(cost),
                balance: formatCredits(balance),
              })}
      </p>
      {state === 'short' && (
        <>
          <p className="text-brand-subtle-foreground">
            {m.creditCostShort()}{' '}
            <Link to="/dashboard/subscription" className="font-medium underline underline-offset-4">
              {m.creditCostGetCredits()}
            </Link>
          </p>
          {pack && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() =>
                requestCreditTopUp({
                  shortfall: balance === undefined ? undefined : cost - balance,
                })
              }
            >
              <Coins />
              {m.creditPackTitle()}
            </Button>
          )}
        </>
      )}
    </div>
  )
}
